import { apiClient, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import { blankToNull, knownWord } from '@/schemas/common'
import {
  FAVOURABLE_DIRECTIONS,
  metricPresentationListSchema,
  type MetricPresentations,
} from '@/schemas/reference'

/**
 * The reference seam: mm-be's presentation table in, app types out.
 *
 * Same shape as `features/mutual-funds/api/fundService.ts`: wire types come
 * from `@/api/schema`, never restated; every mapped result goes through its
 * Zod schema before it leaves the module.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** Exported so it is testable on a fixture without a request. */
export const mapMetricPresentation = (wire: Wire<'Metric'>) => ({
  code: wire.code,
  label: blankToNull(wire.label) ?? wire.code,
  favourable: knownWord(FAVOURABLE_DIRECTIONS, wire.favourable),
  wordAbove: blankToNull(wire.word_above),
  wordBelow: blankToNull(wire.word_below),
  wordAboutTheSame: blankToNull(wire.word_about_the_same),
  explainer: blankToNull(wire.explainer),
  thresholds: wire.thresholds.map((band) => ({
    lower: band.lower,
    upper: band.upper,
    word: blankToNull(band.word),
  })),
})

export const referenceService = {
  /**
   * The whole presentation table, keyed by metric code. One bounded read
   * (mm-be caps it at 500 rows) that every Technical and Trading reading is
   * worded from, cached rather than asked for per card.
   */
  metricPresentations: async (): Promise<MetricPresentations> => {
    const { rows } = await getPage<Wire<'Metric'>>(() =>
      apiClient.GET('/api/v5/reference/metrics/'),
    )
    const presentations = metricPresentationListSchema.parse(rows.map(mapMetricPresentation))
    return Object.fromEntries(presentations.map((metric) => [metric.code, metric]))
  },
}
